<script setup lang="ts">
/**
 * ROND, RONS & RORC (Existing) 통합 패키지 (CSI004). Stage 스테퍼 + 인물별 복제.
 *   Stage 1 ROND(이사별) · Stage 2 RONS(주주별) · Stage 3 RORC(지배자별). 각 부수는 DocScope 로 스코프 격리.
 *   RORC 는 부수마다 개인/법인 양식 — 스코프 값 controller_type 이 'corporate' 면 AR-E-RORC (Corp). 제목줄 토글로 전환.
 */
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import DocScope from '../../aura/agm/cdd/DocScope.vue';
import RondDoc from './RondDoc.vue';
import RonsDoc from './RonsDoc.vue';
import RorcDoc from './RorcDoc.vue';
import RorcCorpDoc from './RorcCorpDoc.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';

type ListKey = 'rond' | 'rons' | 'rorc';
interface Slot { icon: string; name: string; comp: Component; scope: string; id: number; listKey: ListKey; corp?: boolean; }
interface StageDef { no: number; en: string; short: string; toolLabel: string; listKey: ListKey; buildSlots: () => Slot[]; }

const props = defineProps<{ autoLocal?: { rondIds?: number[]; ronsIds?: number[]; rorcIds?: number[] } | null }>();

const fill = useAgmFill();
/** RORC 부수 양식 — 법인 지배자면 Corp 양식 */
function isCorpRorc(id: number) { return String(fill.values[`rorc_${id}/controller_type`] || '').trim() === 'corporate'; }
function setRorcType(id: number, corp: boolean) { fill.setValue(`rorc_${id}/controller_type`, corp ? 'corporate' : 'individual'); }

const rondIds = ref<number[]>([1]);
const ronsIds = ref<number[]>([1]);
const rorcIds = ref<number[]>([1]);
let rondSeq = 1, ronsSeq = 1, rorcSeq = 1;

watch(() => props.autoLocal, (v) => {
  if (!v) return;
  // nominee 0명이면 빈 배열 → 0부(과생성 방지). 빈 배열도 그대로 반영한다.
  if (Array.isArray(v.rondIds)) { rondIds.value = [...v.rondIds]; rondSeq = v.rondIds.length ? Math.max(...v.rondIds) : 0; }
  if (Array.isArray(v.ronsIds)) { ronsIds.value = [...v.ronsIds]; ronsSeq = v.ronsIds.length ? Math.max(...v.ronsIds) : 0; }
  if (Array.isArray(v.rorcIds)) { rorcIds.value = [...v.rorcIds]; rorcSeq = v.rorcIds.length ? Math.max(...v.rorcIds) : 0; }
}, { deep: true, immediate: true });

function idsOf(k: ListKey) { return k === 'rond' ? rondIds : k === 'rons' ? ronsIds : rorcIds; }
function add(k: ListKey) { const r = idsOf(k); if (k === 'rond') rondSeq += 1, r.value.push(rondSeq); else if (k === 'rons') ronsSeq += 1, r.value.push(ronsSeq); else rorcSeq += 1, r.value.push(rorcSeq); }
/** RORC 지배자 추가 — 개인/법인을 골라 추가(부수의 양식이 그걸로 정해진다) */
function addController(corp: boolean) { add('rorc'); setRorcType(rorcSeq, corp); }
function remove(k: ListKey, id: number) { const r = idsOf(k); r.value = r.value.filter((x) => x !== id); } // 0부까지 삭제 허용(nominee 없으면 0부)

const activeStages = computed((): StageDef[] => {
  const defs: Omit<StageDef, 'no'>[] = [
    { en: 'AR-E-ROND.docx', short: 'ROND', toolLabel: '+ Add director', listKey: 'rond',
      buildSlots: () => rondIds.value.map((id) => ({ icon: '👤', name: 'AR-E-ROND.docx', comp: RondDoc, scope: `rond_${id}/`, id, listKey: 'rond' as ListKey })) },
    { en: 'AR-E-RONS.docx', short: 'RONS', toolLabel: '+ Add shareholder', listKey: 'rons',
      buildSlots: () => ronsIds.value.map((id) => ({ icon: '📈', name: 'AR-E-RONS.docx', comp: RonsDoc, scope: `rons_${id}/`, id, listKey: 'rons' as ListKey })) },
    { en: 'AR-E-RORC.docx', short: 'RORC', toolLabel: '+ Add individual / corporate controller', listKey: 'rorc',
      buildSlots: () => rorcIds.value.map((id) => {
        const corp = isCorpRorc(id);
        return { icon: corp ? '🏢' : '🕵️', name: corp ? 'AR-E-RORC (Corp).docx' : 'AR-E-RORC.docx', comp: corp ? RorcCorpDoc : RorcDoc, scope: `rorc_${id}/`, id, listKey: 'rorc' as ListKey, corp };
      }) },
  ];
  return defs.map((s, i) => ({ ...s, no: i + 1 }));
});

const currentStage = ref(1);
const totalStages = computed(() => activeStages.value.length);
function go(n: number) { if (n >= 1 && n <= totalStages.value) currentStage.value = n; }

const view = computed(() => {
  let n = 0;
  return activeStages.value.map((st) => ({ no: st.no, en: st.en, short: st.short, toolLabel: st.toolLabel, listKey: st.listKey, docs: st.buildSlots().map((d) => ({ ...d, no: (n += 1) })) }));
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));

const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() { return { rondIds: [...rondIds.value], ronsIds: [...ronsIds.value], rorcIds: [...rorcIds.value], currentStage: currentStage.value }; }
function restoreLocalState(s: { rondIds?: number[]; ronsIds?: number[]; rorcIds?: number[]; currentStage?: number } | null) {
  if (!s) return;
  if (Array.isArray(s.rondIds)) { rondIds.value = [...s.rondIds]; rondSeq = s.rondIds.length ? Math.max(...s.rondIds) : 0; }
  if (Array.isArray(s.ronsIds)) { ronsIds.value = [...s.ronsIds]; ronsSeq = s.ronsIds.length ? Math.max(...s.ronsIds) : 0; }
  if (Array.isArray(s.rorcIds)) { rorcIds.value = [...s.rorcIds]; rorcSeq = s.rorcIds.length ? Math.max(...s.rorcIds) : 0; }
  if (s.currentStage && s.currentStage >= 1 && s.currentStage <= totalStages.value) currentStage.value = s.currentStage;
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="agm-pkg">
    <div class="stepper">
      <button v-for="st in view" :key="st.no" type="button" class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }" @click="go(st.no)">
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>
    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>
    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage {{ st.no }}</span><span class="stage-t">{{ st.en }}</span></div>
        <div class="agm-toolbar">
          <span class="agm-toolbar-lbl">Add copy (one per person)</span>
          <template v-if="st.listKey === 'rorc'">
            <button type="button" class="agm-tool" @click="addController(false)">+ Add individual controller</button>
            <button type="button" class="agm-tool" @click="addController(true)">+ Add corporate controller</button>
          </template>
          <button v-else type="button" class="agm-tool" @click="add(st.listKey)">{{ st.toolLabel }}</button>
        </div>
        <div v-if="!st.docs.length" class="agm-empty">No copies — nothing will be generated for this register. Use <b>{{ st.toolLabel }}</b> above if needed.</div>
        <div v-for="d in st.docs" :key="d.scope" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span><span class="nm">{{ d.name }}</span>
            <span class="copy">#{{ d.id }}</span>
            <span v-if="d.listKey === 'rorc'" class="rorc-type" :class="{ corp: d.corp }">{{ d.corp ? 'Corporate' : 'Individual' }}</span>
            <button type="button" class="agm-rm" title="Remove copy" @click="remove(d.listKey, d.id)">×</button>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame"><DocScope :scope="d.scope"><component :is="d.comp" /></DocScope></div>
        </div>
      </section>
    </div>
    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ totalStages }}</span>
      <button v-if="currentStage < totalStages" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.agm-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; } .step.active { background: #eaf1ff; border-color: #b9d0ff; } .step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; } .step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; } .step-txt small { font-size: 10px; color: #7a869c; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; } .doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage { width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.agm-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.agm-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; }
.agm-tool { border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; }
.agm-tool:hover { background: #eaf3ff; }
.pkg-doc { width: 100%; } .pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; }
.pkg-doclabel .ic { font-size: 14px; } .pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
/* RORC 부수 구분 표시(선택 아님 — 추가할 때 정해짐) */
.rorc-type { font-size: 10.5px; font-weight: 800; border-radius: 999px; padding: 2px 9px; background: #f2f4f6; color: #6b7a94; }
.rorc-type.corp { background: #fff4e5; color: #b26a00; }
.agm-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; }
.agm-empty { width: 100%; margin: 8px 0 18px; padding: 16px 18px; border: 1px dashed #c5d0e0; border-radius: 12px; background: #f7f9fc; color: #5b6b82; font-size: 13px; box-sizing: border-box; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; } .nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
